import { useNowMinute } from "../../hooks/useNowMinute";
import { useProviderUsage } from "../../usage/useProviderUsage";
import { Skeleton } from "../ui/skeleton";
import { CmmdPlanCard } from "./CmmdPlanCard";
import { ProviderUsageAccountCard } from "./ProviderUsageAccountCard";

/**
 * Every subscription usage is drawn from, in one place.
 *
 * The CMMD plan leads, then each connected personal account with how close it
 * is to its provider's 5-hour and 7-day rate-limit cap, so a user running
 * several accounts knows which one to switch to before hitting a wall mid-task.
 *
 * The plan and the accounts come from different endpoints with different
 * failure modes, so neither waits on the other: a slow plan read would
 * otherwise make live provider figures look broken.
 *
 * Automatic activity reads CMMD's last persisted snapshot without contacting
 * a provider or waking an Environment. A card's Refresh button is the only
 * entry point for a live account-scoped refresh.
 */
export function ProviderUsageTab() {
  useNowMinute();

  return (
    <div className="flex flex-col gap-5">
      <CmmdPlanCard />
      <ProviderAccountGrid />
    </div>
  );
}

function ProviderAccountGrid() {
  const { accounts, isPending, error } = useProviderUsage();

  if (isPending) return <ProviderAccountGridSkeleton />;

  if (error !== null && accounts.length === 0) {
    return <p className="text-muted-foreground text-sm">{error}</p>;
  }

  return (
    <div className="flex flex-col gap-5">
      {error !== null ? <p className="text-warning-foreground text-sm">{error}</p> : null}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {accounts.map(({ account, snapshot }) => {
          return (
            <ProviderUsageAccountCard
              account={account}
              key={`${account.provider}:${account.providerAccountId}`}
              snapshot={snapshot}
            />
          );
        })}
      </div>
    </div>
  );
}

function ProviderAccountGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}
